<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { getShadow } from '$lib/global/helper/shadow';
    import { cn } from '$lib/utils/cn';

    import qs from 'query-string';

    import { getThemedOrCustomColor } from '../_utils/getThemedOrCustomColor';
    import Box from '../_wrappers/Box.svelte';
    import Block from '../Block.svelte';

    import type { Box as BoxType, FlexAlign } from '$lib/types/Components/SharedTypes';
    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { Field } from '$lib/types/Field';
    import type { Language } from '$lib/types/Language';
    import type { ResultMapping } from '$lib/types/ResultMapping';
    import type { BrandBorderRadius, BrandShadow, TransitionEffect, Theme } from '$lib/types/Theme';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';

    export let id = '';
    export let box: BoxType;
    export let verticalAlign: FlexAlign | undefined;
    export let framed = false;
    export let hasShadow = false;
    export let childComponents: Component[] = [];
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let resultMapping: ResultMapping;
    export let theme: Theme;
    export let nextPage = '';
    export let currentPage = '';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let companyId = '';
    export let cookiesEnabled = false;
    export let fields: Field[] = [];
    export let firstResultPage = '';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    const isSingleColumn = layout.columnCount === 1;

    // Optimize bgImage with imgix params for performance.
    // The container max-width comes from Limiter.svelte (desktop variant):
    //   default: 448px (max-w-md), sm (640px): 576px (max-w-xl),
    //   md (768px): 768px, lg (1024px): 1024px, xl (1280px): 1280px, 2xl (1536px): 1536px
    // Visible columns depend on viewport breakpoints (see GridRow.View.svelte):
    //   < md: 1 col, md+: 2 cols (if multi), lg+: 3 cols (if 3), xl+: 4 cols (if >=4)
    const totalColumnCount = layout.columnCount || 1;
    const screenWidth = window?.innerWidth || 1440;

    const getColumnWidth = (): number => {
        // Determine container max-width based on Limiter desktop breakpoints
        let containerWidth: number;

        if (screenWidth >= 1536) containerWidth = 1536;
        else if (screenWidth >= 1280) containerWidth = 1280;
        else if (screenWidth >= 1024) containerWidth = 1024;
        else if (screenWidth >= 768) containerWidth = 768;
        else if (screenWidth >= 640) containerWidth = 576;
        else containerWidth = 448;

        // Determine visible columns based on GridRow grid breakpoints
        let visibleColumns = 1;

        if (totalColumnCount > 1 && screenWidth >= 1280 && totalColumnCount >= 4)
            visibleColumns = 4;
        else if (totalColumnCount > 1 && screenWidth >= 1024 && totalColumnCount === 3)
            visibleColumns = 3;
        else if (totalColumnCount > 1 && screenWidth >= 768) visibleColumns = 2;

        return Math.ceil(containerWidth / visibleColumns);
    };

    const columnWidth = getColumnWidth();

    let optimizedBgImage = framed ? box.bgImage : undefined;
    const bgImageSrc = optimizedBgImage?.src;

    if (optimizedBgImage && bgImageSrc) {
        const defaultImgixParams = {
            auto: 'format,compress',
            w: columnWidth,
            h: undefined,
            dpr: 2,
            q: 80,
        };

        const rawSrc = bgImageSrc.split('?')[0];
        const existingParams = bgImageSrc.split('?')[1];
        const updatedParams = existingParams
            ? { ...qs.parse(existingParams), ...defaultImgixParams }
            : defaultImgixParams;

        optimizedBgImage = {
            ...optimizedBgImage,
            src: `${rawSrc}?${qs.stringify(updatedParams)}`,
        };
    }

    const isThemeV2 = !!theme?.surface;

    const borderRadiusClass = isThemeV2
        ? getBorderRadius('gridColumn', theme?.borderRadius as BrandBorderRadius | undefined)
        : undefined;

    const shadowClass =
        hasShadow && framed && theme?.shadow
            ? getShadow('gridColumn', theme.shadow as BrandShadow)
            : undefined;

    const getFramedBackgroundColor = () => {
        if (!framed) return 'transparent';

        const localBg = box.backgroundColor;

        if (isThemeV2) {
            const defaultBg = getThemedOrCustomColor(undefined, 'backgroundColor', theme);
            const isCustomColor = localBg && localBg !== 'transparent' && localBg !== defaultBg;
            return isCustomColor ? localBg : theme?.surface;
        }

        return getThemedOrCustomColor(localBg || '', 'backgroundColor', theme);
    };

    const columnBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'none',
        left: 'none',
        backgroundColor: getFramedBackgroundColor(),
        bgImage: optimizedBgImage,
    };
</script>

<Box
    {id}
    {...columnBox}
    {transitionDelay}
    {transitionEffect}
    rounded
    roundedClass={borderRadiusClass}
    className={shadowClass ?? ''}
    fullHeight
>
    <div
        class={cn('fun-flex fun-h-full fun-flex-col fun-p-2 sm:fun-p-0', {
            'sm:fun-p-6': !isSingleColumn,
            'fun-p-4 sm:fun-p-10': framed && !isSingleColumn,
            'fun-p-4 sm:fun-p-10 lg:fun-px-0': framed && isSingleColumn,
            'fun-justify-start': verticalAlign === 'start',
            'fun-justify-center': verticalAlign === 'center',
            'fun-justify-end': verticalAlign === 'end',
            'fun-min-h-[375px]': framed && box.bgImage?.visible,
        })}
    >
        {#each childComponents as component, index (component._id)}
            <Block
                id={component._id}
                {language}
                {campaignId}
                {campaignVersionId}
                {companyId}
                {cookiesEnabled}
                componentType={component.componentType}
                childComponents={component.components}
                content={component.content}
                {theme}
                {nextPage}
                {currentPage}
                {resultMapping}
                {fields}
                {firstResultPage}
                {trackingProperties}
                {index}
                layout={{ ...layout, isInColumn: true, isInFramedColumn: framed }}
            />
        {/each}
    </div>
</Box>
